import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../context/AuthContext";

/**
 * <KycPayoutBanner /> + <PayoutBalanceCards />
 *
 * Feb 25 2026 KYC-decouple: businesses become visible + bookable the moment
 * they subscribe. Stripe Connect KYC only gates PAYOUTS — customer money is
 * held safely on the BookFreely platform Stripe account until the business
 * completes verification, then auto-settled via `Transfer.create` (see
 * `_settle_held_funds_to_business` in server.py).
 *
 * This UI:
 *   - Persistent banner shown ONLY while `charges_enabled=false`
 *   - Big "Complete Verification" button that opens Stripe Connect onboarding
 *     (existing `POST /business/connect/onboard` — no new backend endpoint)
 *   - 3-tile summary: Available / Pending / On Hold (Verification Required)
 */

export default function KycPayoutBanner({ user, refreshUser }) {
  const { authHeader } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const refreshBalance = async () => {
    try {
      const r = await axios.get(`${API}/business/balance`, { headers: authHeader() });
      setBalance(r.data);
    } catch (e) {
      // Not fatal — dashboard still renders.
    } finally { setLoading(false); }
  };

  useEffect(() => { refreshBalance(); /* eslint-disable-next-line */ }, []);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const returnUrl = `${window.location.origin}/business/dashboard?stripe_return=1`;
      const r = await axios.post(
        `${API}/business/connect/onboard`,
        { return_url: returnUrl },
        { headers: authHeader() }
      );
      // The API returns `url`. This banner previously read `onboarding_url`,
      // which is never present, so a SUCCESSFUL onboarding call always showed
      // the failure toast and the business could never reach Stripe.
      const onboardingUrl = r.data?.url || r.data?.onboarding_url;
      if (onboardingUrl) {
        window.location.href = onboardingUrl;
      } else {
        toast.error("Couldn't start Stripe onboarding — please try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Stripe onboarding");
    } finally { setStarting(false); }
  };

  const chargesEnabled = balance?.charges_enabled ?? !!user?.stripe_charges_enabled;
  const releaseReady = balance?.release_ready ?? (chargesEnabled && (balance?.payouts_enabled ?? !!user?.stripe_payouts_enabled));
  const onHoldCents = balance?.on_hold_cents ?? 0;
  const onHoldCount = balance?.on_hold_booking_count ?? 0;
  const releasedCents = balance?.released_cents ?? 0;
  const pendingTransferCents = balance?.pending_transfer_cents ?? 0;
  const pendingTransferCount = balance?.pending_transfer_count ?? 0;
  const refundedCents = balance?.refunded_cents ?? 0;
  const refundedCount = balance?.refunded_count ?? 0;

  // Nothing to show until we know either the balance OR the user's KYC state.
  if (loading && !user) return null;

  return (
    <div className="mt-10" data-testid="kyc-payout-panel">
      {!releaseReady && (
        <div
          className="rounded-2xl border-2 p-6 md:flex md:items-center md:justify-between md:gap-6"
          style={{ borderColor: "#f59e0b", background: "rgba(245, 158, 11, 0.08)" }}
          data-testid="kyc-banner"
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span aria-hidden="true" style={{ color: "#b45309", fontSize: 18 }}>&#9888;</span>
              <div className="uppercase text-xs tracking-[0.25em]" style={{ color: "#b45309" }}>
                Action required
              </div>
            </div>
            <h3 className="font-display text-xl mt-1" style={{ color: "var(--gf-text)" }}>
              Set Up Payouts
            </h3>
            <p className="text-sm mt-2" style={{ color: "var(--gf-text-soft)" }}>
              Complete your secure payout setup so you can receive earnings from
              your BookFreely bookings. Customers can already book you and your
              listings are live — your earnings are held safely until setup is
              done, then automatically released to your bank.
            </p>
            {onHoldCount > 0 && (
              <p className="text-xs mt-2" style={{ color: "#b45309" }} data-testid="kyc-banner-hold-note">
                You have <b>${(onHoldCents / 100).toFixed(2)}</b> across {onHoldCount} booking{onHoldCount === 1 ? "" : "s"} waiting to be released to you.
              </p>
            )}
          </div>
          <div className="mt-4 md:mt-0 md:flex-shrink-0">
            <button
              type="button"
              onClick={startOnboarding}
              disabled={starting}
              data-testid="kyc-complete-verification-btn"
              className="rounded-full px-6 py-3 font-medium text-base disabled:opacity-60"
              style={{ background: "#b45309", color: "#fff", minWidth: 220 }}
            >
              {starting ? "Opening secure setup…" : "Complete Verification"}
            </button>
          </div>
        </div>
      )}

      {/* Balance summary tiles — always shown for a business.
          Jun 2026 audit fixes: "Available" used the internal ledger, which no
          code ever credits, so it was structurally always $0.00 under a hint
          that promised payout money. "Pending" was a hardcoded 0. And the third
          card showed the HELD bucket under the label "Settled" once KYC was
          done — always $0.00, because released rows leave that bucket. Each
          card now shows the figure its label describes, all scoped to this
          business's own bookings. */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="payout-balance-cards">
        <BalanceCard
          label="Released to you"
          amountCents={releasedCents}
          testid="balance-released"
          hint="Your share of paid bookings that Stripe has moved to your connected account."
        />
        <BalanceCard
          label="Pending transfer"
          amountCents={pendingTransferCents}
          testid="balance-pending-transfer"
          hint={pendingTransferCount > 0
            ? `${pendingTransferCount} transfer${pendingTransferCount === 1 ? "" : "s"} in progress or retrying.`
            : "No transfers in flight."}
        />
        <BalanceCard
          label={releaseReady ? "On hold" : "On Hold — Verification Required"}
          amountCents={onHoldCents}
          testid="balance-on-hold"
          hint={releaseReady
            ? (onHoldCents > 0
              ? "Awaiting the next release sweep."
              : "Nothing on hold — earnings are released automatically.")
            : "Safely held on your behalf. Released automatically once payout setup is complete."}
          accent={!releaseReady && onHoldCents > 0}
        />
      </div>
      {refundedCents > 0 && (
        <div className="mt-2 text-xs" data-testid="balance-refunded" style={{ color: "var(--gf-text-muted)" }}>
          Refunded to customers: <b>${(refundedCents / 100).toFixed(2)}</b> across {refundedCount} booking{refundedCount === 1 ? "" : "s"} (your share, already reversed — not included in the totals above).
        </div>
      )}
    </div>
  );
}

function BalanceCard({ label, amountCents, hint, testid, accent = false }) {
  const dollars = ((amountCents || 0) / 100).toFixed(2);
  return (
    <div
      className="rounded-2xl border p-4"
      data-testid={testid}
      style={{
        borderColor: accent ? "#f59e0b" : "var(--gf-border)",
        background: accent ? "rgba(245, 158, 11, 0.05)" : "var(--gf-surface)",
      }}
    >
      <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: accent ? "#b45309" : "var(--gf-text-muted)" }}>
        {label}
      </div>
      <div className="font-display text-3xl mt-2" style={{ color: "var(--gf-text)" }}>
        ${dollars}
      </div>
      {hint && (
        <div className="text-xs mt-2" style={{ color: "var(--gf-text-muted)" }}>
          {hint}
        </div>
      )}
    </div>
  );
}
